/**
 * Croatian overrides for the owner screens.
 *
 * Only what genuinely differs from Bosnian. Croatian is ijekavica too, so
 * nothing moves for the jat reflex — what changes is vocabulary: *sustav*,
 * *tvrtka*, *datoteka*, *sigurnost*, *tipka*, and borrowed verbs in -irati.
 *
 * An entry identical to its Bosnian base is a bug, not a no-op: i18n.test.ts
 * fails on one, because a line that changes nothing is a decision that was
 * reverted in bs.ts without anybody revisiting this file.
 *
 * Two words carry most of this file and both recur on every screen:
 *
 *   - **tvrtka** for *firma*, which is the whole domain of this console;
 *   - **račun** for *nalog*, because in Croatian a *nalog* is an instruction
 *     somebody gives, not something you sign in to.
 *
 * After those: *sustav* for *sistem*, *tko* / *nitko* / *što* for
 * *ko* / *niko* / *šta*, *poveznica* for *link*, *gumb* for *dugme*, *zaslon*
 * for *ekran*, *postavke* for *podešavanja*, *spremiti* for *sačuvati*, and
 * *s* rather than *sa* before a consonant.
 */

import type { OwnerSlice } from '../bs/owner'

export const owner: Partial<OwnerSlice> = {
  // ---- Shared vocabulary --------------------------------------------
  'owner.action.save': 'Spremi',
  'owner.form.employeeCode': 'Šifra zaposlenika',
  'owner.action.back': 'Natrag',

  'owner.count.companies.one': '{count} tvrtka',
  'owner.count.companies.few': '{count} tvrtke',
  'owner.count.companies.many': '{count} tvrtki',

  // ---- The console shell --------------------------------------------
  // *odljev* rather than *odliv* — the same ijekavica stem the rest of the
  // file already uses, and the word a Croatian reader expects beside
  // *prihvaćenost*.
  'owner.nav.overview.hint': 'Što se događa na platformi',
  'owner.nav.companies': 'Tvrtke',
  'owner.nav.companies.hint': 'Klijenti i njihove postavke',
  'owner.nav.analytics.hint': 'Prihvaćenost i odljev klijenata',
  'owner.nav.system': 'Sustav',
  'owner.nav.team.hint': 'Tko vodi platformu i s kojim ovlastima',

  'owner.account.aria': 'Račun — {name}',
  'owner.account.myAccount': 'Moj račun',

  // ---- Dashboard ----------------------------------------------------
  'owner.dashboard.subtitle': 'Sve tvrtke zajedno. Osvježava se svakih minut.',
  // *tvrtka*, *njezin*; *spremljen* rather than *sačuvan*; *postavke*.
  'owner.companies.form.currency.hint':
    'U čemu ova tvrtka računa uštede, troškove i nagrade. Postavlja se ovdje pri otvaranju tvrtke, a njezin administrator je poslije može promijeniti sam.',
  'owner.mes.field.token.stored': 'Token je spremljen ({hint}). Ostavite prazno da ga zadržite, upišite „null" da ga obrišete.',
  'owner.activity.prop.settingsChanged': 'Promijenjene postavke',

  // ---- Companies ----------------------------------------------------
  'owner.companies.title': 'Tvrtke',
  'owner.companies.new': 'Nova tvrtka',
  'owner.companies.search.placeholder': 'Ime ili šifra tvrtke',
  'owner.companies.search.aria': 'Pretraga tvrtki',
  'owner.companies.loading': 'Učitavanje tvrtki…',
  'owner.companies.empty.title': 'Nema tvrtki',
  'owner.companies.empty.description': 'Nijedna tvrtka ne odgovara pretrazi.',
  // Has to move with the button it quotes, and the button is *Nova tvrtka*
  // here — a sentence pointing at a control nobody can see is worse than no
  // sentence.
  'owner.companies.empty.hint': 'Kreirajte prvu tvrtku gumbom „Nova tvrtka“.',
  'owner.companies.impersonate.description': 'Odaberite korisnika čiji zaslon želite vidjeti.',

  'owner.companies.saved.created': 'Tvrtka je kreirana.',
  'owner.companies.saved.updated': 'Tvrtka je izmijenjena.',

  'owner.companies.form.description': 'Podaci klijenta i njihove postavke.',
  // *hostira*, not *hostuje* — the -irati family, which is the most
  // productive of the Croatian differences and catches every borrowed verb.
  'owner.companies.form.logo.hint':
    'Adresa slike koju klijent sam hostira. Pojavljuje se u bočnoj traci njihove aplikacije.',
  'owner.companies.form.logo.placeholder': 'https://tvrtka.ba/logo.svg',
  'owner.companies.form.logoDark.hint':
    'Nije obvezno. Logo s tamnim slovima se na tamnoj temi ne vidi — bez ove varijante prikazujemo ga na bijeloj podlozi.',
  'owner.companies.form.logoDark.placeholder': 'https://tvrtka.ba/logo-bijeli.svg',

  'owner.companies.form.admin.legend': 'Administrator tvrtke',
  'owner.companies.form.admin.note':
    'Na ovu adresu šaljemo pozivnicu s poveznicom na kojoj sami postavljaju lozinku. Lozinku ne unosite vi i nitko je ne vidi.',

  // ---- Impersonation ------------------------------------------------
  'owner.impersonate.notice':
    'Sesija traje jedan sat i vidljiva je korisniku. Sve što učinite bilježi se u dnevniku tvrtke pod vašim imenom.',
  'owner.impersonate.empty.description': 'Nijedan račun ne odgovara pretrazi.',

  // ---- Analytics ----------------------------------------------------
  'owner.analytics.monthly.description':
    'Prijave, 5S evaluacije i novi računi, sve tvrtke zajedno.',

  'owner.analytics.types.description': 'Što ljudi zaista prijavljuju.',

  'owner.analytics.contributors.title': 'Tko nosi klijente',

  'owner.analytics.bars.newUsers': 'Novi računi',

  // ---- System health ------------------------------------------------
  'owner.health.loading': 'Provjera sustava…',
  'owner.health.title': 'Sustav',
  'owner.health.queue.oldest.warning':
    'Posao čeka dulje od deset minuta — provjerite radi li kapro-queue.',

  // The MES integration screen. See pages/owner/CompanyMes.tsx for why the
  // secret fields explain what blank means.
  'owner.mes.action.sync': 'Sinkroniziraj sada',
  'owner.mes.field.token.hint': 'Sanctum token koji vam je izdao MES sustav.',

  // ---- Roles and locks ----------------------------------------------
  // *dopušta* rather than *dozvoljava*, and *postavke* throughout for the
  // settings a role may or may not touch.
  'owner.role.full.description': 'Sve: otvara i postavlja tvrtke, obustavlja ih i upravlja timom.',
  'owner.role.support.description':
    'Vidi sve, preuzima račune, prekida sesije i testira MES. Ne mijenja postavke tvrtki.',
  'owner.role.viewer.description': 'Vidi sve, ništa ne mijenja i ne ulazi u tuđe račune.',
  'owner.locked': 'Vaša uloga ({role}) ovo ne dopušta. Mogu: {roles}.',

  // ---- Company list and workspace -----------------------------------
  'owner.companies.filter.aria': 'Status tvrtke',
  'owner.companies.col.company': 'Tvrtka',
  'owner.companies.create': 'Otvori tvrtku',
  'owner.workspace.loading': 'Učitavanje tvrtke…',
  'owner.workspace.back': 'Sve tvrtke',
  'owner.workspace.notFound.title': 'Tvrtka nije pronađena',
  'owner.workspace.notFound.description': 'Možda je poveznica stara ili je adresa pogrešno prepisana.',

  // *ponovno*, not *ponovo*, and *aktivacijski* — the -ijski family.
  'owner.suspend.body':
    'Svi korisnici ove tvrtke odmah se odjavljuju i ne mogu se ponovno prijaviti — ni lozinkom, ni karticom, ni aktivacijskim kodom — dok tvrtku ponovno ne aktivirate. Podaci ostaju netaknuti.',
  'owner.suspend.reason.hint': 'Prikazuje se doslovno na zaslonu za prijavu svakom korisniku tvrtke.',
  'owner.suspend.reason.placeholder': 'npr. Na zahtjev uprave tvrtke, do daljnjega.',
  'owner.reactivate.action': 'Ponovno aktiviraj',
  'owner.reactivate.title': 'Ponovno aktivirati {name}?',
  'owner.reactivate.body':
    'Korisnici se ponovno mogu prijaviti. Sesije prekinute pri obustavi ne vraćaju se — svatko se prijavljuje iznova.',

  // ---- Overview -----------------------------------------------------
  'owner.overview.storage.hint': 'Zbroj veličine svih priloga ove tvrtke.',
  'owner.overview.mes.notConfigured': 'Nije postavljen',
  'owner.overview.mes.moduleOff': 'Postavljen, modul isključen',
  'owner.overview.mes.lastSync': 'Posljednja sinkronizacija {when}',
  'owner.overview.mes.neverSynced': 'Još nijednom sinkroniziran',
  'owner.overview.people.description': 'Od izdanih računa do onih koji ih zaista koriste.',
  'owner.overview.people.total': 'Računa ukupno',
  'owner.overview.activity.description': 'Kada je što posljednji put učinjeno.',
  'owner.overview.activity.lastSignIn': 'Posljednja prijava na sustav',

  // ---- People and sessions ------------------------------------------
  'owner.people.description': 'Preuzimanje računa i prekid sesija bilježe se u dnevniku tvrtke.',
  'owner.people.endSessions.body':
    'Sve otvorene sesije ovog korisnika prekidaju se odmah, na svakom uređaju. Račun ostaje aktivan i može se ponovno prijaviti — za izgubljen telefon to je dovoljno, a račun osobe koja je otišla deaktivira administrator tvrtke.',
  'owner.impersonate.reason.hint':
    '{count} / {max} znakova, najmanje {min}. Upisuje se u dnevnik tvrtke i vidi ga njihov administrator.',
  'owner.sessions.col.device.explain':
    'Pročitano iz podataka preglednika, pa je okvirno. Točan zapis vidite kad zadržite pokazivač; ispod je naziv koji je uređaj sam dao pri prijavi.',
  'owner.sessions.kind.desktop': 'računalo',
  'owner.sessions.empty.title': 'Nitko nije prijavljen',
  'owner.sessions.empty.description': 'Ova tvrtka trenutačno nema nijednu otvorenu sesiju.',
  'owner.sessions.endUser.body.one':
    'Prekida se {count} sesija korisnika {name}. Račun ostaje aktivan i može se ponovno prijaviti.',
  'owner.sessions.endUser.body.few':
    'Prekidaju se {count} sesije korisnika {name}. Račun ostaje aktivan i može se ponovno prijaviti.',
  'owner.sessions.endUser.body.many':
    'Prekida se {count} sesija korisnika {name}. Račun ostaje aktivan i može se ponovno prijaviti.',
  'owner.sessions.endAll.title': 'Odjaviti sve korisnike tvrtke {name}?',
  // *morat će*, written apart — the contracted *moraće* is not Croatian.
  'owner.sessions.endAll.body.one':
    'Sa svih uređaja odjavljuje se {count} osoba (otvorenih sesija: {sessions}). Morat će se ponovno prijaviti; podaci se ne gube.',
  'owner.sessions.endAll.body.few':
    'Sa svih uređaja odjavljuju se {count} osobe (otvorenih sesija: {sessions}). Morat će se ponovno prijaviti; podaci se ne gube.',
  'owner.sessions.endAll.body.many':
    'Sa svih uređaja odjavljuje se {count} osoba (otvorenih sesija: {sessions}). Morat će se ponovno prijaviti; podaci se ne gube.',

  // ---- Activity log -------------------------------------------------
  'owner.activity.title': 'Dnevnik tvrtke',
  'owner.activity.description': 'Izmjene njihovih ljudi i sve što je odavde učinjeno u vezi s njima.',
  'owner.activity.ownerOnly.explain':
    'Obustave, izmjene postavki, preuzimanja računa s razlogom, prekidi sesija i MES testovi — sve što je učinjeno iz ovog panela.',
  'owner.activity.empty.description': 'U dnevniku ove tvrtke još nema ničega.',
  'owner.activity.system': 'Sustav',

  // ---- Access -------------------------------------------------------
  // *rukovoditelji*, *gumb*, *izbornik*, *zaslon* and *spremiti* — the same
  // words the rest of the Croatian interface uses for these.
  'owner.access.modules.description':
    'Koji dijelovi aplikacije postoje za ovu tvrtku. Isključivanje ništa ne briše.',
  'owner.access.module.submissions.off':
    'Kad je isključen: radnici ne mogu prijaviti kvar ni prijedlog, a rukovoditelji ne vide red prijava ni zadatke.',
  'owner.access.module.submissions.hint':
    'Nestaju iz izbornika, kao i gumb za novu prijavu. Ništa se ne briše — uključite ga ponovno i sve je na svom mjestu.',
  'owner.access.module.fiveS.hint':
    'Ocjenjivači gube gumb za novi obilazak, a administrator karticu 5S obrasci. Postojeće ocjene ostaju spremljene.',
  'owner.access.module.analytics.off':
    'Kad je isključen: rukovoditelji ne vide zaslon Analitika svoje tvrtke.',
  'owner.access.module.analytics.hint':
    'Ne utječe na prijave ni na 5S — skriva se samo njihov pregled i trendovi.',
  'owner.access.module.mes.off':
    'Kad je isključen: noćna sinkronizacija i slanje događaja u MES staju.',
  'owner.access.module.mes.hint':
    'Pristupni podaci ostaju spremljeni, a administrator tvrtke ne vidi karticu Integracija.',
  'owner.access.security.description':
    'Kako se ljudi ove tvrtke prijavljuju i koliko dugo ostaju prijavljeni.',
  'owner.access.security.twoFactor.hint':
    'Odnosi se samo na ulogu administratora — radnici i rukovoditelji prijavljuju se kao i dosad.',
  'owner.access.security.password.description':
    'Kad je isključeno, radnici i rukovoditelji prijavljuju se samo QR karticom. Administratori uvijek mogu lozinkom.',
  'owner.access.security.password.hint':
    'Prva prijava aktivacijskim kodom radi uvijek, bez obzira na ovo — tako račun nastaje.',
  'owner.access.security.badge.description':
    'Kad je isključeno, tiskane kartice prestaju raditi i svatko se prijavljuje šifrom i lozinkom.',
  'owner.access.security.badge.hint':
    'Kartice ostaju izdane i ponovno rade čim ovo uključite — ništa ne treba ponovno tiskati.',
  'owner.access.security.sessionDays.hint':
    'Koliko dugo „Zapamti me“ drži korisnika prijavljenim, od 1 do 90. Bez toga sesija traje 12 sati. Vrijedi za nove prijave.',
  'owner.access.limits.description': 'Koliko ljudi i prostora ova tvrtka može koristiti.',
  'owner.access.limits.seats.explain':
    'Kad su mjesta popunjena, administrator tvrtke ne može dodati ni ponovno aktivirati korisnika. Noćna MES sinkronizacija se ne zaustavlja — samo prijavi prekoračenje.',
  'owner.access.limits.storage.hint': 'Zbroj svih priloga tvrtke. Prazno znači bez ograničenja.',
  'owner.access.loss.password':
    'Radnici i rukovoditelji više se ne mogu prijaviti lozinkom — samo karticom.',
  'owner.access.loss.badge': 'Tiskane QR kartice prestaju raditi.',
  'owner.access.loss.seats':
    'Ograničenje ({limit}) je ispod broja aktivnih korisnika ({used}) — nitko se ne deaktivira, ali novi se ne mogu dodati.',
  'owner.access.confirm.title': 'Spremiti promjene za {name}?',
  'owner.access.confirm.body': 'Ovo se odmah odražava na ljude ove tvrtke:',
  'owner.access.confirm.action': 'Spremi promjene',
  'owner.access.dirty': 'Imate nespremljene promjene.',
  'owner.access.clean': 'Sve je spremljeno.',
  'owner.access.saved': 'Pristup je spremljen.',
  'owner.access.help':
    'Ovdje se ništa ne briše: isključen modul i ograničenje samo zatvaraju vrata, a podaci ostaju i vraćaju se čim se promjena poništi. Svaka izmjena bilježi se u dnevniku tvrtke.',

  // ---- Integration, settings, team ----------------------------------
  'owner.mes.moduleOff.body':
    'Postavke su spremljene, ali noćna sinkronizacija i slanje događaja preskaču ovu tvrtku dok se modul ne uključi na kartici Pristup.',
  'owner.mes.locked.edit':
    'Vaša uloga ({role}) može testirati vezu i pokrenuti sinkronizaciju, ali ne i mijenjati podatke. Mijenjati mogu: {editors}.',
  'owner.mes.locked.all':
    'Vaša uloga ({role}) ovdje samo gleda. Testirati i sinkronizirati mogu: {runners}; mijenjati: {editors}.',
  'owner.settings.title': 'Podaci tvrtke',
  'owner.team.subtitle': 'Ljudi koji vode platformu i što tko smije.',
  'owner.team.invite.description': 'Račun u vlasničkom panelu, s ulogom koju odaberete.',
  'owner.team.invite.warning':
    'Svaki član tima vidi podatke svih tvrtki. Što smije mijenjati ovisi o ulozi — dodijelite najmanju koja je dovoljna. Pozivnicu šaljemo na e-mail; lozinku postavlja sam i nitko je ne vidi.',
  'owner.team.panel.description': 'Promjena uloge vrijedi odmah, i u već otvorenim sesijama.',
  'owner.team.lock.lastFull':
    'Ovo je jedini aktivni član s punim pristupom. Prvo nekom drugom dodijelite puni pristup, inače nitko ne bi mogao vratiti promjenu.',
  'owner.team.deactivate.body':
    'Odmah gubi pristup vlasničkom panelu. Račun ostaje i možete ga ponovno aktivirati.',

  // ---- Customer health ----------------------------------------------
  // *ponderirani*, *sustav*, *nitko* / *što*, *napraviti* for *uraditi* — as
  // on the rest of this file — and *poslužitelj* for the server.
  'owner.score.explain':
    'Ponderirani prosjek mjerenja ispod. Mjerenje koje se ne može izračunati ne ulazi u ocjenu, a njegova se težina dijeli ostalima. Računa se najviše jednom u deset minuta.',
  'owner.score.level.healthy.description':
    'Ocjena 70 ili više: koriste sustav, šire ga i odgovaraju na prijave.',
  'owner.score.level.watch.description': 'Ocjena od 45 do 69: koriste sustav, ali nešto popušta.',
  'owner.score.level.dormant.description': 'Nitko ništa nije napravio u posljednjih 30 dana.',
  'owner.score.component.recency.hint':
    'Dani od posljednje aktivnosti bilo koje vrste — prijave, 5S obilaska, recenzije, završenog zadatka ili prijave na sustav. Puni bodovi do 3 dana, nula od 30 dana.',
  'owner.score.component.reach.hint':
    'Udio aktivnih korisnika koji su u posljednjih 30 dana bilo što napravili. Puni bodovi od 60%.',
  'owner.score.component.fiveS.dropped.noData':
    'Tvrtka nema nijedan aktivan radni centar, pa 5S pokrivenost ne ulazi u ocjenu.',
  'owner.score.warning.dormant.never': 'Nitko još nikad ništa nije napravio.',
  'owner.score.warning.lowReach':
    'Samo {percent} aktivnih korisnika napravilo je bilo što u posljednjih 30 dana.',
  'owner.score.warning.mesSyncStale': 'Posljednja MES sinkronizacija bila je prije {duration}.',
  'owner.score.warning.unknown': 'Upozorenje s poslužitelja: {key}',

  // ---- Workspace: analytics -----------------------------------------
  // *tvornica*, *račun*, *razdoblje*, *preglednik*, *zaslon*, *stroj*,
  // *ured* and *mobitel*, where Bosnian says *fabrika*, *nalog*, *period*,
  // *pregledač*, *ekran*, *mašina*, *kancelarija* and *telefon*.
  'owner.insights.window': 'Cijeli mjeseci po lokalnom vremenu tvornice, od {since}',
  'owner.insights.moduleOff.body':
    'Modul „{module}“ je isključen na kartici Pristup, pa ovdje nema što crtati. Podaci iz vremena dok je bio uključen nisu obrisani.',
  'owner.insights.adoption.question':
    'Koriste li ljudi račune koje su dobili — i koliko njih svaki mjesec?',
  'owner.insights.funnel.title': 'Od računa do korištenja',
  'owner.insights.funnel.question': 'Koliko se izdanih računa zaista koristi?',
  'owner.insights.funnel.hint':
    'Aktivni računi koji nisu vlasnički. Aktivan znači da je osoba nešto napravila — prijavu, 5S obilazak, recenziju, završen zadatak ili prijavu na sustav.',
  'owner.insights.funnel.accounts': 'Aktivni računi',
  'owner.insights.funnel.empty': 'Tvrtka nema nijedan aktivan račun.',
  'owner.insights.contributors.empty': 'Nitko nije ništa napravio u ovom razdoblju.',
  'owner.insights.submissions.question': 'Koliko se prijavljuje, i što?',
  'owner.insights.submissions.empty': 'Nijedna prijava u ovom razdoblju.',
  'owner.insights.response.empty': 'Nijedna prijava iz ovog razdoblja nije dobila odgovor.',
  'owner.insights.backlog.hint': 'Stanje danas, bez obzira na odabrano razdoblje.',
  'owner.insights.improvements.hint':
    'Prijedlozi podneseni u odabranom razdoblju i uštede po datumu provedbe, u valuti klijenta.',
  'owner.insights.savings.empty': 'Nijedno poboljšanje nije provedeno u ovom razdoblju.',
  'owner.insights.tasks.hint':
    'Zadaci završeni u odabranom razdoblju, i koliko ih je završeno do roka. Prekoračeni otvoreni zadaci stanje su danas.',
  'owner.insights.tasks.empty': 'Nijedan zadatak u ovom razdoblju.',
  'owner.insights.fiveS.empty': 'Nijedan 5S obilazak u ovom razdoblju.',
  'owner.insights.heatmap.hint':
    'Prijave, 5S obilasci, recenzije i završeni zadaci u posljednjih 90 dana, po lokalnom vremenu tvornice. Prijave na sustav nisu uključene.',
  'owner.insights.heatmap.hours': 'Sat u danu, lokalno vrijeme tvornice',
  'owner.insights.plants.hint':
    'Posljednjih 90 dana. Aktivni ljudi su oni koji su u tom pogonu nešto napravili.',
  'owner.insights.modules.hint':
    'Radnje u posljednjih 30 dana. Prijave: nove prijave i promjene statusa. 5S: obilasci. KaproMES: uspješne sinkronizacije i isporuke, gdje se bilježe. Analitika se ne mjeri — otvaranje zaslona nigdje se ne bilježi.',
  'owner.insights.devices.question': 'Rade li s mobitela uz stroj ili iz ureda?',
  'owner.insights.devices.hint':
    'Sesije otvorene u odabranom razdoblju, prema podacima preglednika. Uređaji se bilježe od {since}.',
  'owner.insights.devices.hint.never':
    'Sesije otvorene u odabranom razdoblju, prema podacima preglednika.',

  // ---- Portfolio ----------------------------------------------------
  // *redak* and *stupac* for a table's row and column, *usporedba*.
  'owner.portfolio.description':
    'Klik na redak otvara klijenta. Klik na naslov stupca sortira po njemu — prvo oni kojima treba pažnja.',
  'owner.portfolio.sort.aria': 'Sortiraj po stupcu: {column}',
  'owner.portfolio.trend.none': 'nema prethodnih 30 dana za usporedbu',
  'owner.system.task.syncMes': 'KaproMES sinkronizacija',
  'owner.system.task.pruneHistory': 'Brisanje stare povijesti',
  'owner.portfolio.legend.moduleOff': 'modul isključen — brojka je od ranije i ne uspoređuje se',
  'owner.portfolio.moduleOff':
    'Modul je za ovog klijenta isključen. Brojka je iz vremena dok je bio uključen i ne uspoređuje se s medijanom.',
  'owner.portfolio.metric.company': 'Tvrtka',
  'owner.portfolio.metric.lastActivity.hint':
    'Bilo što: prijava, 5S obilazak, recenzija, završen zadatak ili prijava na sustav. Zelena točka znači da je netko u aplikaciji upravo sada.',
  'owner.portfolio.platform.description': 'Sve tvrtke zajedno, kroz odabrano razdoblje.',

  // ---- Phase 3: security, sync runs, usage, scheduled tasks -------------
  // The same vocabulary as the rest of this file — *tvrtka*, *račun*,
  // *tko* / *nitko* / *netko* / *što*, *razdoblje*, *sinkronizacija*,
  // *poslužitelj*, *zaslon*, *gumb* — and *točan*, *obvezan*, *povijest*,
  // *postavljen*, *zbroj* and *je li* where Bosnian says *tačan*, *obavezan*,
  // *istorija*, *podešen*, *zbir* and *da li*.
  'owner.security.window': 'Dani po lokalnom vremenu tvornice, od {since}',
  'owner.security.period.aria': 'Razdoblje',
  'owner.security.notRecorded.description':
    'Bilježenje pokušaja prijave počinje s ovom verzijom. Prvi pokušaj nakon toga — uspješan ili ne — pojavit će se ovdje. Do tada nema što prikazati: nula bi značila „nitko nije pogriješio“, a istina je „nitko nije brojao“.',

  'owner.security.stat.succeeded.hint':
    'Prijave koje su završile ulaskom u aplikaciju u odabranom razdoblju, lozinkom ili karticom. Ispod: koliko je različitih ljudi ušlo, od svih aktivnih računa.',
  'owner.security.stat.failed.hint':
    'Pokušaji s pogrešnim podatkom: nepoznata šifra ili kartica, pogrešna lozinka ili kod, neaktivan račun. Ispod: odbijeni (točni podaci, zatvorena vrata) i blokirani (previše pokušaja).',
  'owner.security.stat.rate.hint':
    'Neuspjeli pokušaji od neuspjelih i uspješnih zajedno. Do 15% je uobičajeno jer ljudi griješe u tipkanju; od 30% je ili pogrešno podijeljena lozinka ili netko tko nije njihov.',
  'owner.security.stat.twoFactor.hint':
    'Aktivni administratori s uključenom dvofaktorskom zaštitom. Kad je obvezna, administrator bez nje ne može u administraciju. Svi računi: {covered} od {accounts}.',
  'owner.security.twoFactor.required': 'obvezna',
  'owner.security.twoFactor.optional': 'nije obvezna',
  'owner.security.stat.badges.hint':
    'Aktivni računi kojima je izdana QR kartica za prijavu. Ispod: uspješne prijave lozinkom i karticom u odabranom razdoblju.',
  'owner.security.stat.lastSuccess.hint':
    'Posljednja uspješna prijava bilo koga iz ove tvrtke, bez obzira na odabrano razdoblje.',
  'owner.security.chart.empty': 'Nijedan pokušaj prijave u ovom razdoblju.',

  'owner.security.repeated.question': 'Tko ne može ući — ili tko pokušava tuđim računom?',
  'owner.security.repeated.hint':
    'Ljudi s tri ili više neuspjelih pokušaja u odabranom razdoblju, najviše deset, oni s najviše neuspjeha prvi. Ime otvara osobu na kartici Korisnici.',
  'owner.security.repeated.none': 'Nitko nije imao tri ili više neuspjelih pokušaja.',
  'owner.security.afterFailures.question': 'Je li netko pogodio lozinku?',
  'owner.security.afterFailures.hint':
    'Uspješna prijava kojoj je u satu prije nje prethodilo pet ili više neuspjelih pokušaja na isti račun. Može biti i sama osoba koja se sjetila iz šestog puta — zato je treba pitati.',
  'owner.security.afterFailures.alert':
    'Ovako izgleda pogođena lozinka. Provjerite s tom osobom je li to bila ona; ako nije, prekinite joj sesije i neka promijeni lozinku.',
  'owner.security.ips.hint':
    'IP adrese s tri ili više neuspjelih pokušaja na račune ove tvrtke u odabranom razdoblju, najviše deset. Mnogo računa s jedne adrese znači da netko ide redom po popisu.',
  'owner.security.ips.accounts': 'računa: {count}',
  'owner.security.log.empty.description': 'Za ovu tvrtku još nije zabilježen nijedan pokušaj prijave.',
  'owner.security.log.unknownAccount': 'račun nije pronađen',
  'owner.security.legend.title': 'Što znače ishodi i razlozi',

  'owner.security.outcome.succeeded.hint':
    'Osoba je ušla: podaci su bili točni, a gdje se traži drugi korak, prošla je i njega.',
  'owner.security.outcome.challenged.hint':
    'Lozinka ili kartica je točna i zatražen je kod dvofaktorske zaštite. Ishod drugog koraka bilježi se kao poseban zapis.',
  'owner.security.outcome.failed.hint':
    'Pogrešan podatak — nepoznata šifra ili kartica, pogrešna lozinka ili kod, neaktivan račun. Razlog stoji uz zapis.',
  'owner.security.outcome.refused.hint':
    'Podaci su bili točni, ali ulaz nije dopušten: tvrtka je obustavljena ili je taj način prijave isključen.',
  'owner.security.outcome.throttled.hint':
    'Previše pokušaja u kratkom vremenu, pa poslužitelj odbija bez provjere podataka. Zaštita od pogađanja lozinke; prestaje sama nakon kratkog vremena.',
  'owner.security.reason.unknownAccount.hint':
    'Nijedan račun nema tu šifru zaposlenika. Ponovljeni pokušaji na istu nepoznatu šifru grupiraju se bez čuvanja onoga što je upisano, jer ljudi ponekad upišu lozinku u polje za šifru.',
  'owner.security.reason.wrongPassword.hint': 'Račun postoji, lozinka nije točna.',
  'owner.security.reason.inactiveAccount': 'Neaktivan račun',
  'owner.security.reason.inactiveAccount.hint':
    'Lozinka je točna, ali je račun deaktiviran. Ponovno ga aktivira administrator tvrtke.',
  'owner.security.reason.unknownBadge.hint':
    'Skenirana kartica ne pripada nijednom računu — poništena je, zamijenjena novom ili je tuđa.',
  'owner.security.reason.wrongCode.hint':
    'Šesteroznamenkasti kod iz aplikacije za dvofaktorsku zaštitu nije točan ili je istekao.',
  'owner.security.reason.wrongRecoveryCode.hint': 'Rezervni kod nije točan ili je već iskorišten.',
  'owner.security.reason.challengeExpired.hint':
    'Drugi korak nije završen na vrijeme, ili se račun u međuvremenu promijenio — deaktiviran je ili mu je isključena dvofaktorska zaštita.',
  'owner.security.reason.companySuspended': 'Tvrtka obustavljena',
  'owner.security.reason.companySuspended.hint':
    'Podaci su točni, ali je pristup tvrtki obustavljen. Razlog obustave korisnik vidi na zaslonu za prijavu.',
  'owner.security.reason.methodDisabled.hint':
    'Podaci su točni, ali je taj način prijave — lozinka ili kartica — za ovu tvrtku isključen na kartici Pristup.',

  'owner.mes.runs.title': 'Sinkronizacije',
  'owner.mes.runs.description':
    'Svako pokretanje sinkronizacije — noćno, iz vlasničkog panela ili od administratora tvrtke.',
  'owner.mes.runs.hint':
    'Bilježi se od ove verzije. Aktivacijski kodovi koje je sinkronizacija izdala ovdje se samo broje — nikad se ne čuvaju.',
  'owner.mes.runs.none':
    'Još nijedna zabilježena sinkronizacija. Bilježe se od ove verzije, pa i integracija koja radi mjesecima ovdje počinje praznom; prvo noćno ili ručno pokretanje pojavit će se ovdje.',
  'owner.mes.runs.consecutive.hint':
    'Neuspjela ili prekinuta pokretanja od posljednjeg uspješnog. Jedno je vrijedno pogleda; tri znače da se ljudi i strojevi ne ažuriraju danima.',
  'owner.mes.runs.activations': 'izdano aktivacijskih kodova: {count}',
  'owner.mes.runs.status.running.hint': 'Sinkronizacija je počela i još traje.',
  'owner.mes.runs.status.succeeded.hint':
    'Završena bez greške. Problemi koje je našla — npr. strojevi bez 5S obrasca — navedeni su ispod.',
  'owner.mes.runs.status.interrupted.hint':
    'Pokrenuta prije više od sat vremena i nikad završena — proces je ugašen usred rada, npr. ponovnim pokretanjem poslužitelja.',
  'owner.mes.runs.trigger.command.hint': 'Zakazana sinkronizacija koju poslužitelj pokreće svake noći.',
  'owner.mes.runs.trigger.owner.hint': 'Pokrenuta gumbom „Sinkroniziraj sada“ iz vlasničkog panela.',
  'owner.mes.runs.trigger.admin': 'Administrator tvrtke',
  'owner.mes.runs.trigger.admin.hint': 'Pokrenuta gumbom „Sinkroniziraj sada“ na zaslonu Integracija tvrtke.',

  'owner.usage.history.unavailable': 'Povijest trenutačno nije dostupna.',

  'owner.system.loading': 'Učitavanje povijesti…',
  'owner.system.tasks.col.status.hint':
    'Uspio, Neuspio, Radi, Preskočen (prethodno pokretanje još traje ili ga je preuzeo drugi poslužitelj) ili Prekinut (ugašen usred rada). „Kasni“ ima prednost: zadatak koji se prestao pokretati i dalje pokazuje zeleni ishod posljednjeg pokretanja.',
  'owner.system.tasks.col.next.hint':
    'Prema izrazu rasporeda, u njegovoj vremenskoj zoni. Točno vrijeme je ispod pokazivača.',
  'owner.system.tasks.status.skipped.hint':
    'Raspored ga je preskočio jer prethodno pokretanje još traje ili ga je preuzeo drugi poslužitelj — tako je i zamišljeno.',

  'owner.system.mes.title': 'KaproMES sinkronizacije',
  'owner.system.mes.question': 'Stiže li noćna sinkronizacija do svakog klijenta koji je ima?',
  'owner.system.mes.panel': 'Klijenti s postavljenim MES-om',
  'owner.system.mes.hint':
    'Svi klijenti s postavljenom adresom MES-a, po nazivu. Naziv otvara njihovu karticu Integracija s cijelom poviješću.',
  'owner.system.mes.empty': 'Nijedan klijent nema postavljen KaproMES.',

  'owner.system.storage.question': 'Koliko brzo se disk puni, i tko ga puni?',
  'owner.system.storage.total.hint': 'Zbroj svih priloga svih klijenata, sada.',
  'owner.system.storage.growth.hint':
    'Zbroj dnevnog rasta svih klijenata — pravac kroz njihovih posljednjih 30 dana. Potrebno je najmanje sedam dnevnih zapisa.',

  'owner.system.signIns.question': 'Tko pokušava ući, u koju tvrtku — i pokušava li netko ući u naš tim?',
  'owner.system.signIns.owners.hint':
    'Računi našeg tima otvaraju svakog klijenta, pa je i jedan neuspjeh vrijedan pogleda, a pet ili blokada je hitno.',
  'owner.system.signIns.owners.none': 'Nijedan neuspjeli pokušaj na račune vlasnika u odabranom razdoblju.',
  'owner.system.signIns.owners.failed':
    'Neuspjelih pokušaja na račune vlasnika: {failed} · blokirano: {throttled}',
  'owner.system.signIns.unknown.hint':
    'Pokušaji koji nisu odgovarali nijednom računu, pa ne pripadaju nijednoj tvrtki. Ovdje se prvo vidi netko tko pogađa.',
  'owner.system.signIns.codes.key': 'Ključ pokušaja — ne otkriva što je upisano',
  'owner.system.signIns.byCompany.hint':
    'Svaki klijent po nazivu, za odabrano razdoblje. Naziv otvara njihovu karticu Sigurnost.',

  // ---- Phase 4: the overview and alerts ---------------------------------
  // *sustav*, *račun*, *tvrtka*, *nitko* / *netko* / *što*, *razdoblje*,
  // *sinkronizacija*, *popis*, *postavke*, *spremljeno* — the same families
  // as the rest of this file.
  'owner.dashboard.system.aria': 'Stanje sustava',
  'owner.dashboard.system.open': '{label}: {value}. Otvori stranicu Sustav.',

  'owner.dashboard.pulse.hint':
    '„Isti dani prošlog mjeseca“ znači od prvog do današnjeg dana u mjesecu, da se pola mjeseca ne uspoređuje s cijelim. Linija je posljednjih 30 dana, po lokalnom vremenu.',
  'owner.dashboard.pulse.signIns': 'Prijave u sustav',
  'owner.dashboard.pulse.activePeople.hint':
    'Različiti ljudi klijenata koji su u razdoblju nešto napravili ili se uspješno prijavili.',
  'owner.dashboard.pulse.newUsers': 'Novi računi',
  'owner.dashboard.pulse.newUsers.hint': 'Računi klijenata otvoreni u razdoblju.',

  'owner.dashboard.activity.title': 'Što je tim radio',
  'owner.dashboard.activity.impersonating.hint':
    'Napravljeno dok je vlasnik bio prijavljen kao netko od ljudi klijenta.',
  'owner.dashboard.activity.open': 'Otvori dnevnik tvrtke {name}',

  'owner.alerts.state.attention.hint': 'Otvoreno, a nitko ga još nije preuzeo ni odgodio.',
  'owner.alerts.state.acknowledged.hint':
    'Netko iz tima se time bavi. Ako se stanje pogorša, vraća se u „Treba pažnju“.',

  'owner.alerts.mute.body':
    'Utišano upozorenje se zatvara i ne otvara se ponovo dok se utišavanje ne ukine. Provjera i dalje radi — samo se o njoj više ne javlja, ni ovdje ni e-poštom.',
  'owner.alerts.mute.reason.hint': 'Vidi ga ostatak tima na popisu utišanih.',

  'owner.alerts.empty.attention': 'Posljednja provjera, {when}, nije našla ništa što čeka da ga netko preuzme.',
  'owner.alerts.empty.never':
    'Provjere se još nisu pokrenule. Pokreću se svakih deset minuta — ako ovo stoji duže, pogledajte raspored na stranici Sustav.',
  'owner.alerts.empty.acknowledged': 'Nitko trenutno ništa nije preuzeo.',

  'owner.alerts.type.health.neverActivated': 'Računi koji nikad nisu korišteni',
  'owner.alerts.type.health.mesSyncStale': 'MES sinkronizacija stoji',
  'owner.alerts.type.security.ownerSignInFailures': 'Neuspjele prijave na račune vlasnika',
  'owner.alerts.type.security.scanningAddress': 'Jedna adresa pokušava više računa',
  'owner.alerts.type.mes.syncFailing': 'MES sinkronizacija ne uspijeva',

  'owner.alerts.fact.signInAfterFailures': 'Račun {code}: {failures}, pa uspješna prijava {when}, s adrese {ip}.',
  'owner.alerts.fact.signInAfterFailures.noIp': 'Račun {code}: {failures}, pa uspješna prijava {when}.',
  'owner.alerts.fact.repeatedFailures': 'Račun {code}: {failures} u posljednja 24 sata, posljednji {when}.',
  'owner.alerts.fact.ownerSignInFailures':
    'Računi vlasnika u posljednja 24 sata — neuspjelih pokušaja: {failed}, blokiranih: {throttled}. Posljednji {when}.',
  'owner.alerts.fact.scanningAddress':
    'Adresa {ip} u posljednja 24 sata — neuspjelih pokušaja: {failures}, različitih računa: {accounts}. Posljednji {when}.',

  'owner.alertPrefs.loading': 'Učitavanje postavki…',
  'owner.alertPrefs.immediate.description':
    'E-mail čim se otvori hitno upozorenje koje nitko nije preuzeo ni odgodio — jedan po provjeri, koliko god ih bilo.',
  'owner.alertPrefs.digest.description':
    'Svako jutro u 7:30: što je otvoreno, što je preuzeto ili odgođeno, i što se otvorilo i zatvorilo za 24 sata. Ne stiže kad nema što reći.',
  'owner.alertPrefs.noEmail':
    'Na vašem računu nema e-mail adrese, pa ova upozorenja ne mogu stići. Dodajte je na stranici Moj račun.',
  'owner.alertPrefs.saved': 'Spremljeno.',
}
